/* global ezstandalone */
import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Share2, BookOpen } from "lucide-react";

// Appwrite imports
import { Client, Databases, Storage, Query } from "appwrite";

// Initialize Appwrite client
const client = new Client();
client
  .setEndpoint("https://cloud.appwrite.io/v1")
  .setProject("67fbee6a001260770104");

const databases = new Databases(client);
const storage = new Storage(client);

const DATABASE_ID = "68098bc80034d68e4e64";
const COLLECTION_ID = "68098bd900226cbeb1cd";
const BUCKET_ID = "680990d6000f00f9d400";

const Poems = () => {
  const [poems, setPoems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareModal, setShareModal] = useState(null);

  useEffect(() => {
    const fetchPoems = async () => {
      setLoading(true);
      try {
        const response = await databases.listDocuments(
          DATABASE_ID,
          COLLECTION_ID,
          [Query.equal("isApproved", true), Query.orderDesc("$createdAt")]
        );

        // Only include documents with contentType === "Poem" or where it's undefined (backward compatibility)
        const filteredPoems = response.documents.filter(
          (doc) => !doc.contentType || doc.contentType.toLowerCase() === "poem"
        );

        setPoems(filteredPoems);
      } catch (error) {
        console.error("Error fetching poems:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPoems();
  }, []);

  const getImageUrl = (imageId) => {
    if (!imageId) return null;
    try {
      return storage.getFileView(BUCKET_ID, imageId);
    } catch (error) {
      console.error("Error generating image URL:", error);
      return "";
    }
  };

  const generateShareLink = (poemId) => {
    return `${window.location.origin}/poem/${poemId}`;
  };

  const handleShare = (poem) => {
    setShareModal(poem);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        alert("Link copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  const shareViaWhatsApp = (poem) => {
    const shareLink = generateShareLink(poem.$id);
    const message = `Check out this beautiful poem "${poem.title}" on PoemXtra: ${shareLink}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const shareViaTwitter = (poem) => {
    const shareLink = generateShareLink(poem.$id);
    const message = `"${poem.title}" - A beautiful poem on PoemXtra`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      message
    )}&url=${encodeURIComponent(shareLink)}`;
    window.open(twitterUrl, "_blank");
  };

  const shareViaFacebook = (poem) => {
    const shareLink = generateShareLink(poem.$id);
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      shareLink
    )}`;
    window.open(facebookUrl, "_blank");
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 3,
    initialSlide: 0,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          infinite: true,
          dots: true,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          initialSlide: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

useEffect(() => {
    // Wait for component to mount and DOM to be ready
    const initializeEzoicAds = () => {
      if (window.ezstandalone && window.ezstandalone.cmd) {
        window.ezstandalone.cmd.push(function () {
          // Pass ALL placement IDs in a single call (as per Ezoic docs)
          window.ezstandalone.showAds(171, 164);
        });
      }
    };

    // Small delay to ensure DOM elements are rendered
    const timer = setTimeout(initializeEzoicAds, 300);
    
    return () => clearTimeout(timer);
  }, []);

        

  return (
    <>
      <section
        id="poems"
        className="py-16 px-6"
        style={{ backgroundColor: "#f5f5dc" }}
      >
        <h3 className="text-3xl font-semibold text-center mb-6">
          Featured Poems
        </h3>

         {/* Top Ad - after title, before content */}
        <div id="ezoic-pub-ad-placeholder-171"></div>

        {loading ? (
          <div className="text-center py-10">
            <p>Loading poems...</p>
          </div>
        ) : poems.length > 0 ? (
          <Slider {...settings}>
            {poems.map((poem) => (
              <div
                key={poem.$id}
                className="bg-white shadow-lg rounded-lg hover:shadow-xl transition-shadow mx-2 overflow-hidden"
              >
                <div className="w-full aspect-[3/2] bg-gray-100 flex items-center justify-center">
                  {poem.imageId ? (
                    <img
                      src={getImageUrl(poem.imageId)}
                      alt={poem.title}
                      className="w-full h-full object-contain object-center"
                      onError={(e) => {
                        console.error("Image failed to load:", poem.imageId);
                        e.target.src =
                          "https://via.placeholder.com/400x200?text=Image+Not+Found";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-gray-400 italic">No image</span>
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <h2 className="text-xl font-bold mb-2">{poem.title}</h2>
                  <p className="text-gray-700 mb-4 whitespace-pre-line line-clamp-4">
                    {poem.content}
                  </p>

                  <div className="flex justify-between items-center mt-3">
                    {/* Read More button */}
                    <button
                      onClick={() => window.open(`/poem/${poem.$id}`, "_blank")}
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                    >
                      <BookOpen size={16} />
                      Read More
                    </button>

                    {/* Share button */}
                    <button
                      onClick={() => handleShare(poem)}
                      className="flex items-center gap-1 text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors"
                    >
                      <Share2 size={16} />
                      Share
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        ) : (
          <div className="text-center py-10">
            <p>No approved poems found. Check back soon!</p>
          </div>
        )}
      </section>

       {/* Bottom Ad - after main content */}
    
      <div id="ezoic-pub-ad-placeholder-164"></div>

      {/* Share Modal */}
      {shareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">
              Share "{shareModal.title}"
            </h3>

            {/* Share Link */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Share Link:
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={generateShareLink(shareModal.$id)}
                  readOnly
                  className="flex-1 p-2 border rounded text-sm"
                />
                <button
                  onClick={() =>
                    copyToClipboard(generateShareLink(shareModal.$id))
                  }
                  className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600 transition-colors text-sm"
                >
                  Copy
                </button>
              </div>
            </div>

            {/* Social Share Buttons */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Share on:
              </label>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => shareViaWhatsApp(shareModal)}
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors text-sm"
                >
                  WhatsApp
                </button>
                <button
                  onClick={() => shareViaTwitter(shareModal)}
                  className="bg-blue-400 text-white px-4 py-2 rounded hover:bg-blue-500 transition-colors text-sm"
                >
                  Twitter
                </button>
                <button
                  onClick={() => shareViaFacebook(shareModal)}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors text-sm"
                >
                  Facebook
                </button>
              </div>
            </div>

            {/* Close Button */}
            <div className="flex justify-end">
              <button
                onClick={() => setShareModal(null)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Poems;
